/**
 * Tax Flow — F5-C: Staff Missing-Document Advisory UI
 *
 * Placement: WorkspaceDocsTab → between PackageIntelligencePanel and KPI grid.
 * Consumes certified F5-A / F5-B APIs. No new detection, no LLM, no client comms.
 * Staff-only — never rendered in Secure Portal routes.
 *
 * Endpoints consumed (read-only):
 *   GET  /api/document-advisories?client_id=&limit=200
 *   GET  /api/document-advisories/{id}/reasoning
 *
 * Endpoints invoked (write, staff-governed):
 *   POST /api/document-advisories/{id}/review
 *   POST /api/document-advisories/{id}/dismiss
 *   POST /api/document-advisories/{id}/reasoning/regenerate  (server-authoritative 403)
 *
 * Views: Active (default) | Reviewed | Dismissed | Resolved
 * "Reviewed" = lifecycle_state:detected AND reviewed_at set — NOT a lifecycle state.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SectionCard, EmptyState, LoadingState, AlertBanner } from "@/components/shared";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

/* ── Severity display maps ─────────────────────────────────────────────────── */
const SEV_BADGE = {
  critical: "bg-rose-100 text-rose-700 border border-rose-200",
  high:     "bg-amber-100 text-amber-700 border border-amber-200",
  medium:   "bg-blue-100 text-blue-700 border border-blue-200",
  low:      "bg-slate-100 text-slate-500 border border-slate-200",
};
const SEV_DOT = {
  critical: "bg-rose-500",
  high:     "bg-amber-400",
  medium:   "bg-blue-400",
  low:      "bg-slate-300",
};
const SEV_WEIGHT = { critical: 0, high: 1, medium: 2, low: 3 };

/* ── Canonical dismiss reasons (must match F5-A DISMISS_REASONS) ───────────── */
const DISMISS_REASONS = [
  { value: "not_required",       label: "Not required" },
  { value: "already_received",   label: "Already satisfied elsewhere" },
  { value: "filed_elsewhere",    label: "Filed elsewhere" },
  { value: "client_not_subject", label: "Client not subject to this requirement" },
  { value: "false_positive",     label: "False positive / incorrect detection" },
  { value: "duplicate_advisory", label: "Duplicate advisory" },
  { value: "other",              label: "Other (requires explanation)" },
];
const DISMISS_LABEL = Object.fromEntries(DISMISS_REASONS.map((r) => [r.value, r.label]));

/* ── Reasoning assessment display ──────────────────────────────────────────── */
const ASSESS_CFG = {
  required:              { label: "Required",               cls: "bg-rose-50 text-rose-700" },
  likely_required:       { label: "Likely Required",        cls: "bg-amber-50 text-amber-700" },
  needs_review:          { label: "Needs Staff Review",     cls: "bg-blue-50 text-blue-700" },
  probably_not_required: { label: "Probably Not Required",  cls: "bg-slate-100 text-slate-600" },
};

/* ── Views ─────────────────────────────────────────────────────────────────── */
const VIEWS = [
  { key: "active",    label: "Active" },
  { key: "reviewed",  label: "Reviewed" },
  { key: "converted", label: "Converted" },
  { key: "dismissed", label: "Dismissed" },
  { key: "resolved",  label: "Resolved" },
];

/* ── Helpers ───────────────────────────────────────────────────────────────── */
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
}

function sortAdvisories(arr) {
  return [...arr].sort((a, b) => {
    const sw = (SEV_WEIGHT[a.severity] ?? 2) - (SEV_WEIGHT[b.severity] ?? 2);
    if (sw !== 0) return sw;
    const ty = parseInt(b.tax_year || "0", 10) - parseInt(a.tax_year || "0", 10);
    if (ty !== 0) return ty;
    return (b.created_at || "") > (a.created_at || "") ? 1 : -1;
  });
}

/* ── Reasoning status chip ─────────────────────────────────────────────────── */
function ReasoningChip({ status }) {
  const cfg = {
    queued:     { label: "Queued",          cls: "bg-slate-100 text-slate-500" },
    processing: { label: "Processing",      cls: "bg-blue-50 text-blue-600", spin: true },
    completed:  { label: "Reasoned",        cls: "bg-violet-50 text-violet-700" },
    failed:     { label: "Reason failed",   cls: "bg-rose-50 text-rose-600" },
  }[status];
  if (!cfg) return null;
  return (
    <span className={cn("inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium", cfg.cls)}>
      {cfg.spin && <Icons.Loader2 className="h-2.5 w-2.5 animate-spin" />}
      {cfg.label}
    </span>
  );
}

/* ── Reasoning status inline message ───────────────────────────────────────── */
function ReasoningInline({ adv }) {
  const notNeeded = adv.needs_reasoning === false || adv.reasoning_status === "not_applicable";
  if (notNeeded) {
    return (
      <div className="text-[11px] text-slate-400 italic" data-testid={`advisory-deterministic-msg-${adv.advisory_id}`}>
        Deterministic requirement — no Allison reasoning required.
      </div>
    );
  }
  if (adv.reasoning_status === "queued") {
    return (
      <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
        <Icons.Clock className="h-3 w-3" />Reasoning queued…
      </span>
    );
  }
  if (adv.reasoning_status === "processing") {
    return (
      <span className="text-[11px] text-blue-500 inline-flex items-center gap-1">
        <Icons.Loader2 className="h-3 w-3 animate-spin" />Reasoning in progress…
      </span>
    );
  }
  if (adv.reasoning_status === "failed") {
    return (
      <span className="text-[11px] text-rose-500 inline-flex items-center gap-1" data-testid={`advisory-reasoning-failed-${adv.advisory_id}`}>
        <Icons.AlertTriangle className="h-3 w-3" />Reasoning failed — advisory remains actionable
      </span>
    );
  }
  return null;
}

/* ── Dismiss confirmation dialog ───────────────────────────────────────────── */
function DismissDialog({ target, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [note, setNote]     = useState("");
  const [busy, setBusy]     = useState(false);

  useEffect(() => { if (!target) { setReason(""); setNote(""); } }, [target]);

  const confirm = async () => {
    if (!reason) { toast.warning("Please select a dismiss reason"); return; }
    if (reason === "other" && !note.trim()) { toast.warning("A note is required when selecting Other"); return; }
    setBusy(true);
    try {
      await api.post(`/document-advisories/${target.advisory_id}/dismiss`, {
        dismiss_reason: reason,
        dismiss_note: note.trim() || undefined,
      });
      toast.success("Advisory dismissed");
      onDone();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.reason : detail) || "Could not dismiss advisory");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="advisory-dismiss-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.XCircle className="h-4 w-4 text-amber-500" />
            Dismiss Advisory
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-slate-700">{target?.label}</span> — dismissed advisories are
            permanently retained for audit and visible in the Dismissed view.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Dismiss Reason <span className="text-rose-500">*</span>
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="advisory-dismiss-reason-select"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 bg-white"
            >
              <option value="">Select a reason…</option>
              {DISMISS_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Note {reason === "other" ? <span className="text-rose-500">*</span> : <span className="text-slate-400">(optional)</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={reason === "other"
                ? "Required — explain why this advisory does not apply…"
                : "Optional notes for the audit record…"}
              data-testid="advisory-dismiss-note"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            data-testid="advisory-dismiss-cancel"
            className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={busy || !reason}
            data-testid="advisory-dismiss-confirm"
            className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors"
          >
            {busy && <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Dismiss Advisory
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Evidence dialog ───────────────────────────────────────────────────────── */
function EvidenceDialog({ target, onClose }) {
  const evidence = target?.supporting_evidence || [];
  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="advisory-evidence-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.Search className="h-4 w-4 text-blue-500" />
            Supporting Evidence
          </DialogTitle>
          <DialogDescription>{target?.label}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-1">
          {evidence.length === 0 ? (
            <div className="text-sm text-slate-400 py-4 text-center" data-testid="advisory-evidence-empty">
              No evidence references recorded for this advisory.
            </div>
          ) : (
            evidence.map((e, i) => (
              <div
                key={i}
                className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5"
                data-testid={`advisory-evidence-item-${i}`}
              >
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-mono">{e.type}</span>
                  <span className="text-[10px] text-slate-400 font-mono break-all">{e.source_ref}</span>
                </div>
                <div className="text-sm text-slate-700">{e.description}</div>
              </div>
            ))
          )}
          <div className="text-[11px] text-slate-400 pt-1 border-t border-slate-50">
            Evidence references are deterministic — no raw document content is shown here.
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            data-testid="advisory-evidence-close"
            className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── AI Decision Trace dialog ──────────────────────────────────────────────── */
function TraceDialog({ target, onClose }) {
  const r      = target?.reasoning;
  const trace  = r?.ai_decision_trace;

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="advisory-trace-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.BrainCircuit className="h-4 w-4 text-violet-600" />
            AI Decision Trace
          </DialogTitle>
          <DialogDescription>{target?.advisory?.label}</DialogDescription>
        </DialogHeader>

        <div className="py-1 space-y-3">
          {target?.loading && (
            <div className="flex items-center gap-2 py-6 text-slate-500 text-sm justify-center">
              <Icons.Loader2 className="h-4 w-4 animate-spin" />Loading trace…
            </div>
          )}
          {target?.error && (
            <div className="text-sm text-rose-600 py-2" data-testid="advisory-trace-error">{target.error}</div>
          )}
          {!target?.loading && !target?.error && !r && (
            <div className="text-sm text-slate-400 py-6 text-center" data-testid="advisory-trace-no-reasoning">
              No Allison reasoning has been recorded for this advisory.
            </div>
          )}
          {r && (
            <div className="space-y-3 text-sm" data-testid="advisory-trace-content">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Assessment</div>
                  <span className={cn("inline-flex px-2 py-0.5 rounded text-xs font-medium",
                    ASSESS_CFG[r.assessment]?.cls || "bg-slate-100 text-slate-600")}>
                    {ASSESS_CFG[r.assessment]?.label || r.assessment}
                  </span>
                </div>
                <div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Confidence</div>
                  <div className="text-slate-700 capitalize">
                    {(r.confidence?.band || "").replace(/_/g, " ")}
                    {r.confidence?.score != null ? ` (${Math.round(r.confidence.score * 100)}%)` : ""}
                  </div>
                </div>
              </div>

              {r.confidence?.explanation && (
                <div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Confidence Explanation</div>
                  <div className="text-slate-600 text-[12px]">{r.confidence.explanation}</div>
                </div>
              )}

              {r.reasoning_summary && (
                <div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Summary</div>
                  <div className="text-slate-700">{r.reasoning_summary}</div>
                </div>
              )}

              {r.recommended_staff_action && (
                <div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Recommended Staff Action</div>
                  <div className="text-slate-700">{r.recommended_staff_action}</div>
                </div>
              )}

              {(r.missing_evidence || []).length > 0 && (
                <div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Missing Evidence</div>
                  <ul className="list-disc list-inside space-y-0.5 text-slate-600 text-[12px]">
                    {r.missing_evidence.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}

              {trace && (
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 space-y-1.5" data-testid="advisory-ai-trace-block">
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Trace Metadata</div>
                  <div className="text-[11px] text-slate-600">
                    Engine: {trace.reasoning_engine_version || "—"} · Model: {trace.model_used || "—"}
                  </div>
                  {trace.escalated && (
                    <div className="text-[11px] text-amber-600">
                      Escalated: {trace.escalation_reason || "complexity trigger"}
                    </div>
                  )}
                  {(trace.applicable_policies || []).length > 0 && (
                    <div className="text-[11px] text-slate-500 space-y-0.5">
                      {trace.applicable_policies.map((p, i) => <div key={i}>• {p}</div>)}
                    </div>
                  )}
                  {(trace.inputs_consumed || []).length > 0 && (
                    <div className="text-[11px] text-slate-400">
                      Inputs: {trace.inputs_consumed.join(", ")}
                    </div>
                  )}
                </div>
              )}

              <div className="text-[10px] text-slate-400">
                Version {r.version} · {r.model_used} · {fmtDate(r.created_at)}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            data-testid="advisory-trace-close"
            className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Converted-to-request metadata ────────────────────────────────────────── */
function ConvertedInfo({ adv, onViewRequest }) {
  return (
    <div
      className="text-[11px] text-emerald-700 bg-emerald-50 rounded-lg px-2.5 py-2 flex items-center justify-between gap-2"
      data-testid={`advisory-converted-info-${adv.advisory_id}`}
    >
      <div className="flex items-center gap-1.5">
        <Icons.FilePlus className="h-3 w-3 shrink-0" />
        <span>
          Document request created{adv.converted_at ? ` ${fmtDate(adv.converted_at)}` : ""}
        </span>
      </div>
      <button
        onClick={onViewRequest}
        data-testid={`advisory-view-request-btn-${adv.advisory_id}`}
        className="text-[11px] text-emerald-700 font-medium hover:underline inline-flex items-center gap-1 shrink-0"
      >
        View Request <Icons.ArrowDown className="h-3 w-3" />
      </button>
    </div>
  );
}

/* ── Convert-to-request dialog ─────────────────────────────────────────────── */
function ConvertToRequestDialog({ target, onClose, onDone }) {
  const suggestedText = target
    ? `Please provide the following document so we can continue our review:\n\n${target.label}\n\nThis document may be needed to support the work included in your engagement.`
    : "";

  const [requestText, setRequestText] = useState("");
  const [dueDate,     setDueDate]     = useState("");
  const [staffNote,   setStaffNote]   = useState("");
  const [busy,        setBusy]        = useState(false);

  // Sync prefill when dialog opens
  useEffect(() => {
    if (target) { setRequestText(suggestedText); setDueDate(""); setStaffNote(""); }
  }, [target?.advisory_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async () => {
    if (!requestText.trim()) { toast.warning("Request text is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/document-advisories/${target.advisory_id}/convert-to-request`, {
        request_text: requestText.trim(),
        due_date: dueDate.trim() || undefined,
        staff_note: staffNote.trim() || undefined,
      });
      toast.success("Document request created");
      onDone(r.data.document_request_id);
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = (typeof detail === "object" ? detail?.code : null) || "";
      if (code === "request_already_exists") {
        toast.warning("An equivalent open document request already exists for this advisory");
      } else if (code === "advisory_already_converted") {
        toast.info("This advisory has already been converted to a request");
        onDone(detail?.document_request_id);
        onClose();
      } else if (e.response?.status === 403) {
        toast.error("Permission denied — client_success/approve access required");
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Could not create request");
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="advisory-convert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.FilePlus className="h-4 w-4 text-emerald-600" />
            Request Document
          </DialogTitle>
          <DialogDescription>
            Creates a canonical Document Request from this advisory.
            The client will see this in their secure portal. Review the text before confirming.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Advisory context (read-only) */}
          <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
            <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-500">
              {target?.doc_type_code && (
                <span className="font-mono">{target.doc_type_code}</span>
              )}
              {target?.tax_year && <span>TY {target.tax_year}</span>}
              {target?.jurisdiction && <span className="uppercase">{target.jurisdiction}</span>}
            </div>
          </div>

          {/* Client-facing request text (editable) */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Client-Facing Request Text <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={requestText}
              onChange={(e) => setRequestText(e.target.value)}
              rows={5}
              data-testid="advisory-convert-request-text"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-y"
            />
            <div className="text-[10px] text-slate-400 mt-0.5">
              This text is shown to the client in their secure portal.
            </div>
          </div>

          {/* Optional due date */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Due Date <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              data-testid="advisory-convert-due-date"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
            />
          </div>

          {/* Internal staff note */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Internal Staff Note <span className="text-slate-400 font-normal">(not shown to client)</span>
            </label>
            <textarea
              value={staffNote}
              onChange={(e) => setStaffNote(e.target.value)}
              rows={2}
              placeholder="Internal context for audit record…"
              data-testid="advisory-convert-staff-note"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            data-testid="advisory-convert-cancel"
            className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={busy || !requestText.trim()}
            data-testid="advisory-convert-confirm"
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors"
          >
            {busy && <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create Document Request
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AdvisoryCard({ adv, onReviewed, onDismiss, onEvidence, onTrace, onRegenerate, onConvert, onViewRequest, busy }) {
  const isDetected   = adv.lifecycle_state === "detected";
  const isDismissed  = adv.lifecycle_state === "dismissed";
  const isResolved   = adv.lifecycle_state === "resolved";
  const isConverted  = adv.lifecycle_state === "converted_to_request";
  const isReviewed   = !!adv.reviewed_at;
  const canTrace     = adv.reasoning_status === "completed";
  const canRegen     = adv.reasoning_status === "completed" || adv.reasoning_status === "failed";
  const canConvert   = isDetected && !!adv.doc_type_code;

  return (
    <div
      className={cn(
        "rounded-xl border bg-white px-4 py-3 space-y-2 transition-colors",
        adv.severity === "critical" ? "border-rose-200" :
        adv.severity === "high"     ? "border-amber-200" : "border-slate-200"
      )}
      data-testid={`advisory-card-${adv.advisory_id}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <span className={cn("mt-1 h-2.5 w-2.5 rounded-full shrink-0", SEV_DOT[adv.severity] || SEV_DOT.low)} />
          <div className="min-w-0">
            <div
              className="text-sm font-medium text-slate-800 leading-snug"
              data-testid={`advisory-label-${adv.advisory_id}`}
            >
              {adv.label}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
              {adv.doc_type_code && (
                <span className="text-[10px] font-mono text-slate-400">{adv.doc_type_code}</span>
              )}
              {adv.tax_year && (
                <span className="text-[10px] text-slate-400">TY {adv.tax_year}</span>
              )}
              {adv.jurisdiction && (
                <span className="text-[10px] text-slate-400 uppercase">{adv.jurisdiction}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={cn("text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide",
              SEV_BADGE[adv.severity] || SEV_BADGE.low)}
            data-testid={`advisory-severity-${adv.advisory_id}`}
          >
            {adv.severity || "medium"}
          </span>
          <ReasoningChip status={adv.reasoning_status} />
        </div>
      </div>

      {/* Reasoning inline message (deterministic / queued / processing / failed) */}
      <ReasoningInline adv={adv} />

      {/* Reviewed metadata */}
      {isReviewed && (
        <div className="text-[11px] text-slate-400" data-testid={`advisory-reviewed-info-${adv.advisory_id}`}>
          <Icons.CheckCheck className="h-3 w-3 inline mr-1 text-blue-400" />
          Reviewed {fmtDate(adv.reviewed_at)}
        </div>
      )}

      {/* Dismissed metadata */}
      {isDismissed && (
        <div
          className="text-[11px] text-slate-500 bg-slate-50 rounded-lg px-2.5 py-2"
          data-testid={`advisory-dismiss-info-${adv.advisory_id}`}
        >
          <div>Dismissed {fmtDate(adv.dismissed_at)}</div>
          <div className="text-slate-400">
            Reason: {DISMISS_LABEL[adv.dismiss_reason] || adv.dismiss_reason || "—"}
          </div>
          {adv.dismiss_note && <div className="text-slate-400 italic mt-0.5">"{adv.dismiss_note}"</div>}
        </div>
      )}

      {/* Resolved metadata */}
      {isResolved && (
        <div className="text-[11px] text-emerald-600" data-testid={`advisory-resolved-info-${adv.advisory_id}`}>
          <Icons.CheckCircle2 className="h-3 w-3 inline mr-1" />
          Resolved {fmtDate(adv.resolved_at)}
          {adv.resolution_source && <span className="text-slate-400 ml-1">· {adv.resolution_source.replace(/_/g, " ")}</span>}
        </div>
      )}

      {/* Converted metadata (F5-D) */}
      {isConverted && (
        <ConvertedInfo adv={adv} onViewRequest={onViewRequest} />
      )}

      {/* Action row */}
      <div className="flex items-center gap-3 pt-0.5 flex-wrap" data-testid={`advisory-actions-${adv.advisory_id}`}>
        {isDetected && !isReviewed && (
          <button
            onClick={() => onReviewed(adv)}
            disabled={!!busy}
            data-testid={`advisory-mark-reviewed-${adv.advisory_id}`}
            className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Icons.CheckCheck className="h-3 w-3" />Mark Reviewed
          </button>
        )}
        {/* F5-D: Request Document — eligible active advisories */}
        {canConvert && (
          <button
            onClick={() => onConvert(adv)}
            disabled={!!busy}
            data-testid={`advisory-request-doc-btn-${adv.advisory_id}`}
            className="text-[11px] text-emerald-600 hover:underline inline-flex items-center gap-1 font-medium disabled:opacity-50"
          >
            <Icons.FilePlus className="h-3 w-3" />Request Document
          </button>
        )}
        {isDetected && (
          <button
            onClick={() => onDismiss(adv)}
            disabled={!!busy}
            data-testid={`advisory-dismiss-btn-${adv.advisory_id}`}
            className="text-[11px] text-amber-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Icons.XCircle className="h-3 w-3" />Dismiss
          </button>
        )}
        <button
          onClick={() => onEvidence(adv)}
          data-testid={`advisory-evidence-btn-${adv.advisory_id}`}
          className="text-[11px] text-slate-500 hover:underline inline-flex items-center gap-1"
        >
          <Icons.Search className="h-3 w-3" />View Evidence
        </button>
        {canTrace && (
          <button
            onClick={() => onTrace(adv)}
            data-testid={`advisory-trace-btn-${adv.advisory_id}`}
            className="text-[11px] text-violet-600 hover:underline inline-flex items-center gap-1"
          >
            <Icons.BrainCircuit className="h-3 w-3" />AI Decision Trace
          </button>
        )}
        {/* Regenerate: shown when reasoning exists; server enforces management/admin gate (403 handled) */}
        {canRegen && isDetected && (
          <button
            onClick={() => onRegenerate(adv)}
            disabled={!!busy}
            data-testid={`advisory-regenerate-btn-${adv.advisory_id}`}
            className="text-[11px] text-slate-400 hover:text-slate-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Icons.RefreshCw className="h-3 w-3" />Regenerate Reasoning
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Main panel ─────────────────────────────────────────────────────────────── */
export default function MissingDocumentAdvisoriesPanel({ clientId }) {
  const [advisories, setAdvisories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [view,       setView]       = useState("active");
  const [busyId,     setBusyId]     = useState(null);

  /* Dialog state */
  const [dismissTarget,  setDismissTarget]  = useState(null);
  const [evidenceTarget, setEvidenceTarget] = useState(null);
  const [traceState,     setTraceState]     = useState(null);
  const [convertTarget,  setConvertTarget]  = useState(null);  // F5-D

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/document-advisories?client_id=${clientId}&limit=200`);
      setAdvisories(r.data.advisories || []);
      setError(null);
    } catch {
      setError("Advisory data unavailable — other document features remain fully operational.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  /* Client-side filtering */
  const filtered = useMemo(() => {
    let arr = [];
    if (view === "active")    arr = advisories.filter((a) => a.lifecycle_state === "detected");
    if (view === "reviewed")  arr = advisories.filter((a) => a.lifecycle_state === "detected" && !!a.reviewed_at);
    if (view === "converted") arr = advisories.filter((a) => a.lifecycle_state === "converted_to_request");
    if (view === "dismissed") arr = advisories.filter((a) => a.lifecycle_state === "dismissed");
    if (view === "resolved")  arr = advisories.filter((a) => a.lifecycle_state === "resolved");
    return sortAdvisories(arr);
  }, [advisories, view]);

  const counts = useMemo(() => ({
    active:    advisories.filter((a) => a.lifecycle_state === "detected").length,
    reviewed:  advisories.filter((a) => a.lifecycle_state === "detected" && !!a.reviewed_at).length,
    converted: advisories.filter((a) => a.lifecycle_state === "converted_to_request").length,
    dismissed: advisories.filter((a) => a.lifecycle_state === "dismissed").length,
    resolved:  advisories.filter((a) => a.lifecycle_state === "resolved").length,
  }), [advisories]);

  /* ── Action handlers ─────────────────────────────────────────────────────── */
  const handleReviewed = async (adv) => {
    setBusyId(adv.advisory_id);
    try {
      await api.post(`/document-advisories/${adv.advisory_id}/review`);
      setAdvisories((prev) =>
        prev.map((a) => a.advisory_id === adv.advisory_id
          ? { ...a, reviewed_at: new Date().toISOString() }
          : a));
      toast.success("Marked as reviewed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark reviewed");
    } finally { setBusyId(null); }
  };

  const handleDismissDone = useCallback(() => { load(); }, [load]);

  /* F5-D: conversion done — update advisory state locally + reload */
  const handleConvertDone = useCallback((documentRequestId) => {
    setAdvisories((prev) =>
      prev.map((a) => a.advisory_id === convertTarget?.advisory_id
        ? { ...a, lifecycle_state: "converted_to_request",
            converted_to_request_id: documentRequestId,
            converted_at: new Date().toISOString() }
        : a));
    load();
  }, [convertTarget, load]);

  /* F5-D: scroll to "Requested Documents" section */
  const handleViewRequest = useCallback(() => {
    const el = document.querySelector('[data-testid="ws-doc-requests-section"]');
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleTrace = async (adv) => {
    setTraceState({ advisory: adv, reasoning: null, loading: true, error: null });
    try {
      const r = await api.get(`/document-advisories/${adv.advisory_id}/reasoning`);
      const versions = r.data.versions || [];
      setTraceState({ advisory: adv, reasoning: versions[0] || null, loading: false, error: null });
    } catch {
      setTraceState({ advisory: adv, reasoning: null, loading: false, error: "Could not load reasoning data." });
    }
  };

  const handleRegenerate = async (adv) => {
    setBusyId(adv.advisory_id);
    try {
      await api.post(`/document-advisories/${adv.advisory_id}/reasoning/regenerate`);
      toast.success("New reasoning version queued");
      load();
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Permission denied — management or admin access required for regeneration");
      } else {
        toast.error(e.response?.data?.detail || "Could not regenerate reasoning");
      }
    } finally { setBusyId(null); }
  };

  /* ── Render ──────────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="mb-5" data-testid="advisory-panel-loading">
        <LoadingState label="Loading advisories…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-5" data-testid="advisory-panel-error">
        <AlertBanner tone="warning" title="Missing Document Advisories">{error}</AlertBanner>
      </div>
    );
  }

  return (
    <div className="mb-5" data-testid="missing-doc-advisories-panel">
      <SectionCard
        icon="AlertTriangle"
        title="Missing Document Advisories"
        action={counts.active > 0 ? (
          <span
            className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium"
            data-testid="advisory-active-count-badge"
          >
            {counts.active} active
          </span>
        ) : null}
      >
        {/* View filter tabs */}
        <div className="flex items-center gap-0.5 mb-4 border-b border-slate-100 pb-2" data-testid="advisory-view-tabs">
          {VIEWS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              data-testid={`advisory-view-tab-${key}`}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors",
                view === key
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              )}
            >
              {label}
              {counts[key] > 0 && (
                <span className={cn(
                  "text-[10px] px-1 rounded-full font-medium min-w-[16px] text-center",
                  view === key ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"
                )}>
                  {counts[key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Advisory list */}
        {filtered.length === 0 ? (
          <EmptyState
            icon={view === "active" ? "CheckCircle2" : "Inbox"}
            title={view === "active" ? "No active advisories" : `No ${view} advisories`}
            description={
              view === "active"
                ? "No missing document advisories detected for this client."
                : undefined
            }
          />
        ) : (
          <div className="space-y-2" data-testid="advisory-cards-list">
            {filtered.map((adv) => (
              <AdvisoryCard
                key={adv.advisory_id}
                adv={adv}
                busy={busyId === adv.advisory_id}
                onReviewed={handleReviewed}
                onDismiss={(a) => setDismissTarget(a)}
                onEvidence={(a) => setEvidenceTarget(a)}
                onTrace={handleTrace}
                onRegenerate={handleRegenerate}
                onConvert={(a) => setConvertTarget(a)}
                onViewRequest={handleViewRequest}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Dialogs */}
      <DismissDialog
        target={dismissTarget}
        onClose={() => setDismissTarget(null)}
        onDone={handleDismissDone}
      />
      <EvidenceDialog
        target={evidenceTarget}
        onClose={() => setEvidenceTarget(null)}
      />
      <TraceDialog
        target={traceState}
        onClose={() => setTraceState(null)}
      />
      {/* F5-D: convert-to-request dialog */}
      <ConvertToRequestDialog
        target={convertTarget}
        onClose={() => setConvertTarget(null)}
        onDone={handleConvertDone}
      />
    </div>
  );
}
